/**
 * UserProfileContext - Provides global user profile state management
 * Manages user profile data, loading states, and profile operations
 * Integrates with SessionManager for profile synchronization
 */

import { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { UserProfile, UserProfileError } from '../types/user';
import { userProfileService } from '../services/api/UserProfileService';
import { sessionManager } from '../services/user/SessionManager';
import { useAuthContext } from './AuthContext';

/**
 * User profile context value interface
 * Defines the shape of data and actions available through the context
 */
export interface UserProfileContextValue {
  /** Current user profile data */
  profile: UserProfile | null;
  /** Loading state for profile operations */
  isLoading: boolean;
  /** Error state for profile operations */
  error: Error | null;
  /** Refresh profile data from backend */
  refreshProfile: () => Promise<void>;
  /** Update profile data */
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

/**
 * User profile context
 */
const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined);

/**
 * Props for UserProfileProvider
 */
interface UserProfileProviderProps {
  /** Child components */
  children: ReactNode;
}

/**
 * UserProfileProvider component
 * Provides user profile state and actions to all child components
 * Requires AuthProvider to be a parent component
 * 
 * @param props - Provider props
 * @returns Provider component
 */
export function UserProfileProvider({ children }: UserProfileProviderProps) {
  const { isAuthenticated, user, idToken } = useAuthContext();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Debug: Log profile changes
  useEffect(() => {
    console.log('[UserProfileContext] Profile state changed:', {
      hasProfile: !!profile,
      compositionsCount: profile?.compositions?.length,
      aiGenerateCount: profile?.aiGenerateCount,
      manualComposeCount: profile?.manualComposeCount,
      compositionEditCount: profile?.compositionEditCount,
      compositionShareCount: profile?.compositionShareCount
    });
  }, [profile]);

  /**
   * Refresh profile data from backend
   * Fetches the latest profile data and updates state
   */
  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated || !idToken) {
      setProfile(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profileData = await userProfileService.getProfile();
      setProfile(profileData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch profile');
      setError(error);
      console.error('Error fetching user profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, idToken]);

  /**
   * Update profile data
   * Updates profile on backend and refreshes local state
   * 
   * @param updates - Partial profile updates (preferences, metadata)
   */
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!isAuthenticated || !idToken) {
      throw new UserProfileError(
        'NOT_AUTHENTICATED' as any,
        'User must be authenticated to update profile',
        401
      );
    }

    setIsLoading(true);
    setError(null);

    try {
      const updatedProfile = await userProfileService.updateProfile(updates);
      setProfile(updatedProfile);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update profile');
      setError(error);
      console.error('Error updating user profile:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, idToken]);

  /**
   * Auto-refresh profile when authentication state changes
   * Syncs profile on sign-in, clears on sign-out
   * Uses SessionManager to get profile data
   */
  useEffect(() => {
    if (isAuthenticated && user && idToken) {
      // User just signed in, check SessionManager for profile
      const checkProfile = async () => {
        const sessionState = sessionManager.getSessionState();
        
        console.log('[UserProfileContext] Checking profile from session:', {
          hasProfile: !!sessionState.profile,
          compositionsCount: sessionState.profile?.compositions?.length,
          aiGenerateCount: sessionState.profile?.aiGenerateCount
        });
        
        if (sessionState.profile) {
          // Profile already synced by SessionManager
          console.log('[UserProfileContext] Setting profile from session state');
          setProfile(sessionState.profile);
          setError(sessionState.error || null);
          setIsLoading(false);
        } else {
          // Profile not yet synced, wait longer for auth to complete
          // This handles the race condition where useAuth syncs profile
          // but this effect runs before it completes
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const retrySessionState = sessionManager.getSessionState();
          console.log('[UserProfileContext] Retry - checking profile from session:', {
            hasProfile: !!retrySessionState.profile,
            compositionsCount: retrySessionState.profile?.compositions?.length
          });
          
          if (retrySessionState.profile) {
            console.log('[UserProfileContext] Setting profile from retry session state');
            setProfile(retrySessionState.profile);
            setError(retrySessionState.error || null);
            setIsLoading(false);
          } else {
            // Still no profile, fetch it
            console.log('[UserProfileContext] No profile in session, fetching...');
            setIsLoading(true);
            try {
              await refreshProfile();
            } catch (err) {
              // Error already handled in refreshProfile
              console.log('Profile fetch failed, will retry on next render');
            }
          }
        }
      };
      
      checkProfile();
    } else {
      // User signed out, clear profile
      setProfile(null);
      setError(null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, idToken]); // Removed refreshProfile from deps to prevent infinite loop

  const contextValue: UserProfileContextValue = {
    profile,
    isLoading,
    error,
    refreshProfile,
    updateProfile,
  };

  return (
    <UserProfileContext.Provider value={contextValue}>
      {children}
    </UserProfileContext.Provider>
  );
}

/**
 * Hook to access user profile context
 * Must be used within UserProfileProvider
 * 
 * @returns User profile state and actions
 * @throws Error if used outside UserProfileProvider
 */
export function useUserProfileContext(): UserProfileContextValue {
  const context = useContext(UserProfileContext);
  
  if (context === undefined) {
    throw new Error('useUserProfileContext must be used within UserProfileProvider');
  }
  
  return context;
}
